import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Trophy, Share2, Users, Sparkles, Crown, Flame, X } from "lucide-react";

/**
 * Six distinct badge visuals — one per (category × rank):
 *   category ∈ {"sharer", "referrer"}
 *   rank     ∈ {1, 2, 3}
 *
 * Design rules:
 *   • Sharers use CYAN → PINK spectrum (movement / spread)
 *   • Referrers use GOLD → GREEN spectrum (growth / money)
 *   • Rank 1 is filled + heavy glow; Rank 2 mid; Rank 3 outline-only
 *
 * The badge itself is a compact chip. When placed on a video (pass
 * `mode="video"`), it lights up with a 10-second pulse and then
 * fades out — grabs the eye without permanently cluttering the card.
 * Clicking the badge triggers a full-screen celebration animation.
 */

const VARIANTS = {
  sharer: {
    label: "Top Sharer",
    icon: Share2,
    // rank -> { ring, bg, text, glow, title, subtitle }
    ranks: {
      1: {
        ring: "#FF007F",
        bg: "linear-gradient(135deg, #FF007F 0%, #00FFFF 100%)",
        text: "#000",
        glow: "0 0 22px rgba(255,0,127,0.9), 0 0 40px rgba(0,255,255,0.6)",
        title: "#1 TOP SHARER",
        subtitle: "The unstoppable signal",
        icon: Crown,
      },
      2: {
        ring: "#FF007F",
        bg: "rgba(255,0,127,0.18)",
        text: "#FF007F",
        glow: "0 0 14px rgba(255,0,127,0.65)",
        title: "#2 TOP SHARER",
        subtitle: "Silver spreader",
        icon: Share2,
      },
      3: {
        ring: "#00FFFF",
        bg: "transparent",
        text: "#00FFFF",
        glow: "0 0 10px rgba(0,255,255,0.55)",
        title: "#3 TOP SHARER",
        subtitle: "Bronze broadcaster",
        icon: Share2,
      },
    },
  },
  referrer: {
    label: "Top Referrer",
    icon: Users,
    ranks: {
      1: {
        ring: "#FFC300",
        bg: "linear-gradient(135deg, #FFC300 0%, #39FF14 100%)",
        text: "#000",
        glow: "0 0 22px rgba(255,195,0,0.9), 0 0 40px rgba(57,255,20,0.55)",
        title: "#1 TOP REFERRER",
        subtitle: "Kingmaker of MatrixShopping",
        icon: Crown,
      },
      2: {
        ring: "#FFC300",
        bg: "rgba(255,195,0,0.20)",
        text: "#FFC300",
        glow: "0 0 14px rgba(255,195,0,0.65)",
        title: "#2 TOP REFERRER",
        subtitle: "Silver recruiter",
        icon: Users,
      },
      3: {
        ring: "#39FF14",
        bg: "transparent",
        text: "#39FF14",
        glow: "0 0 10px rgba(57,255,20,0.55)",
        title: "#3 TOP REFERRER",
        subtitle: "Bronze downline builder",
        icon: Users,
      },
    },
  },
};

/** The small chip. Sizes: "sm" (video overlay), "md" (profile). */
export function CreatorBadge({
  category,
  rank,
  mode = "static",
  size = "sm",
  onClick,
}) {
  const [visible, setVisible] = useState(true);
  const [pulsing, setPulsing] = useState(mode === "video");

  // Video-mode badges pulse for 10s then fade away — spec says the badge
  // "lights up and pulses for the first 10 seconds and disappears".
  useEffect(() => {
    if (mode !== "video") return;
    setVisible(true);
    setPulsing(true);
    const pulseOff = setTimeout(() => setPulsing(false), 10000);
    const fadeOut = setTimeout(() => setVisible(false), 10600);
    return () => { clearTimeout(pulseOff); clearTimeout(fadeOut); };
  }, [mode]);

  if (!visible) return null;
  const cfg = VARIANTS[category]?.ranks?.[rank];
  if (!cfg) return null;

  const Icon = cfg.icon;
  const isSm = size === "sm";
  const dims = isSm
    ? "h-8 w-8 text-[9px]"
    : "h-11 w-11 text-[11px]";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`creator-badge-${category}-${rank}`}
      style={{
        background: cfg.bg,
        borderColor: cfg.ring,
        boxShadow: pulsing ? cfg.glow : "none",
        color: cfg.text,
        animation: pulsing ? "badgePulse 900ms ease-in-out infinite" : "none",
        transition: "opacity 0.6s ease-out",
        opacity: pulsing || mode !== "video" ? 1 : 0.85,
      }}
      className={`relative flex items-center justify-center border-2 font-mono font-bold ${dims}`}
      title={cfg.title}
      aria-label={cfg.title}
    >
      <Icon className={isSm ? "h-3.5 w-3.5" : "h-5 w-5"} strokeWidth={2.5} />
      <span
        className="absolute -bottom-1 -right-1 bg-black border font-bold leading-none px-1 py-0.5 text-[8px]"
        style={{ borderColor: cfg.ring, color: cfg.ring }}
      >
        {rank}
      </span>
    </button>
  );
}

/** Full-screen celebration overlay — shown when a badge is tapped. */
export function BadgeCelebration({ category, rank, username, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 4200);
    return () => clearTimeout(t);
  }, [onClose]);
  const cfg = VARIANTS[category]?.ranks?.[rank];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return createPortal(
    <div
      data-testid="badge-celebration-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-md"
      onClick={onClose}
    >
      {/* Confetti-like radial rays */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 24 }).map((_, i) => (
          <span
            key={i}
            className="absolute left-1/2 top-1/2"
            style={{
              width: 2,
              height: 200,
              background: cfg.ring,
              opacity: 0.35,
              transformOrigin: "top center",
              transform: `translate(-50%, 0) rotate(${(i * 15)}deg)`,
              animation: `badgeRayShoot 1200ms cubic-bezier(0.2, 0.8, 0.2, 1) both`,
              animationDelay: `${i * 30}ms`,
            }}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={onClose}
        className="absolute top-6 right-6 h-10 w-10 flex items-center justify-center text-white/70 hover:text-white"
        aria-label="Close"
      >
        <X className="h-5 w-5" />
      </button>

      <div
        className="relative text-center px-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="mx-auto h-32 w-32 flex items-center justify-center border-4 rounded-none"
          style={{
            background: cfg.bg,
            borderColor: cfg.ring,
            boxShadow: cfg.glow,
            animation: "badgeHeroPop 900ms cubic-bezier(0.34, 1.56, 0.64, 1) both",
          }}
        >
          <Icon className="h-14 w-14" style={{ color: cfg.text }} strokeWidth={2.5} />
        </div>
        <p
          className="mt-6 font-mono text-xs uppercase tracking-[0.4em]"
          style={{ color: cfg.ring }}
        >
          // {VARIANTS[category].label}
        </p>
        <h1
          className="font-display font-black text-4xl sm:text-5xl mt-2"
          style={{ color: "#fff", textShadow: cfg.glow }}
        >
          {cfg.title}
        </h1>
        <p className="mt-4 text-white/70 text-sm">{cfg.subtitle}</p>
        {username && (
          <p className="mt-3 font-mono text-xs uppercase tracking-widest text-white/50">
            earned by <span style={{ color: cfg.ring }}>@{username}</span>
          </p>
        )}
        <div className="mt-6 flex items-center justify-center gap-2 text-white/40 text-[10px] font-mono uppercase tracking-widest">
          <Sparkles className="h-3 w-3" /> tap anywhere to close
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Combined helper: badge + built-in celebration modal on click. */
export function BadgeWithCelebration(props) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <CreatorBadge {...props} onClick={() => setOpen(true)} />
      {open && (
        <BadgeCelebration
          category={props.category}
          rank={props.rank}
          username={props.username}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export default CreatorBadge;
